import { CloseOutlined } from '@ant-design/icons';
import { DatePicker, Input, Modal } from 'antd';
import dayjs from 'dayjs';
import { useContext, useEffect } from 'react';

import { PaymentModalTypeEnum } from '@app/components/RecordModal/components/RecordPayment/types';
import { DATE_FORMATS, MAX_FIELD_LENGTH_5000 } from '@app/constants';
import { PREFIX } from '@app/constants/appPrefix';
import { DATE_PICKER_FORMAT } from '@app/constants/datePickerFormat';
import { rangePickerLocale } from '@app/constants/rangePickerLocale';
import { NotificationContext } from '@app/providers';
import { handleCatchError } from '@app/services';
import { divideAmount } from '@app/utils';

import type { PaymentModalProps } from '../types';
import type { Dayjs } from 'dayjs';

import '../styles/PaymentModal.desktop.scss';

const { TextArea } = Input;

const DEFAULT_MODAL_CONFIG = {
  title: 'с баланса',
  modalDataTestId: 'payment-from-balance',
  titleTestId: 'payment-from-balance-title',
  contentTextTestId: 'payment-from-balance-text',
};

const MODAL_CONFIG: Record<
  PaymentModalTypeEnum,
  {
    title: string;
    modalDataTestId: string;
    titleTestId: string;
    contentTextTestId: string;
  }
> = {
  CARD: {
    title: 'картой',
    modalDataTestId: 'card-payment-popup',
    titleTestId: 'card-payment-title',
    contentTextTestId: 'card-payment-text',
  },
  CASH: {
    title: 'наличными',
    modalDataTestId: 'cash-payment-popup',
    titleTestId: 'cash-payment-title',
    contentTextTestId: 'payment-cash',
  },
  BALANCE: DEFAULT_MODAL_CONFIG,
  PREPAYMENT: DEFAULT_MODAL_CONFIG,
  BANK: DEFAULT_MODAL_CONFIG,
  YOO_KASSA: {
    title: 'через ЮKassa',
    modalDataTestId: 'yoo-kassa-payment-popup',
    titleTestId: 'yoo-kassa-payment-title',
    contentTextTestId: 'yoo-kassa-cash',
  },
};

export const PaymentModal: React.FC<PaymentModalProps> = ({
  isPaymentModalOpen,
  onOk,
  onCancel,
  receiptId,
  prepaymentLoader,
  paymentAmount,
  paymentModalType,
  recordDate,
  setPrepaymentLoader,
  paymentDate,
  setPaymentDate,
  paymentComment,
  setPaymentComment,
  paymentMethod,
  handlePayByYouKassa,
}) => {
  const { showNotification } = useContext(NotificationContext);

  const isPaymentModalWithDate =
    paymentModalType !== null &&
    [
      PaymentModalTypeEnum.CARD,
      PaymentModalTypeEnum.CASH,
      PaymentModalTypeEnum.YOO_KASSA,
    ].includes(paymentModalType);

  useEffect(() => {
    if (recordDate) {
      setPaymentDate(
        dayjs(recordDate)
          .startOf('day')
          .format(DATE_FORMATS.ISO_FORMAT_NO_TIMEZONE)
      );
    }
  }, [recordDate, setPaymentDate]);

  const modalData = paymentModalType
    ? MODAL_CONFIG[paymentModalType]
    : DEFAULT_MODAL_CONFIG;

  const setDefaultData = () => {
    setPaymentComment('');
    recordDate && setPaymentDate(recordDate);
  };

  const handleCancel = () => {
    onCancel();
    setDefaultData();
  };

  const handleOk = async () => {
    try {
      setPrepaymentLoader(true);
      await onOk(receiptId);
      setDefaultData();
    } catch (error) {
      showNotification(handleCatchError(error));
    } finally {
      setPrepaymentLoader(false);
    }
  };

  return (
    <Modal
      data-testid={modalData.modalDataTestId}
      className={`${PREFIX}payment-modal`}
      zIndex={1001}
      title={
        <span
          data-testid={modalData.titleTestId}
        >{`Оплата записи ${modalData.title}`}</span>
      }
      open={isPaymentModalOpen}
      okText={<span data-testid="confirm">Подтвердить</span>}
      cancelText={<span data-testid="cancel">Отменить</span>}
      onCancel={handleCancel}
      onOk={
        paymentModalType === PaymentModalTypeEnum.YOO_KASSA
          ? () =>
              handlePayByYouKassa({
                Amount: parseFloat(
                  paymentAmount
                    .replace(/\s/g, '')
                    .replace(',', '.')
                    .replace(/[^\d.]/g, '')
                ),
                PaymentMethod: paymentMethod || '',
                IsNeedToCreatePayment: true,
              })
          : handleOk
      }
      okButtonProps={{
        type: 'primary',
        loading: prepaymentLoader,
        disabled: prepaymentLoader,
      }}
      cancelButtonProps={{ disabled: prepaymentLoader }}
      closeIcon={<CloseOutlined data-testid="close-popup" />}
    >
      <span data-testid={modalData.contentTextTestId}>
        {`Оплатить запись ${modalData.title} на сумму ${divideAmount(
          paymentAmount
        )}?`}
      </span>
      {isPaymentModalWithDate && (
        <div className="content-container">
          <h4 data-testid="payment-date">Дата оплаты</h4>
          <DatePicker
            data-testid="date-for-payment-value"
            locale={rangePickerLocale}
            inputReadOnly={true}
            format={DATE_PICKER_FORMAT}
            size="large"
            allowClear={false}
            onChange={(date: Dayjs) => {
              if (date) {
                setPaymentDate(
                  date.format(DATE_FORMATS.ISO_FORMAT_NO_TIMEZONE)
                );
              }
            }}
            defaultValue={dayjs(paymentDate)}
            value={dayjs(paymentDate)}
            classNames={{ popup: { root: 'calendar-block' } }}
          />
          <h4 data-testid="comment-title">Комментарий</h4>
          <TextArea
            data-testid="comment-input"
            value={paymentComment}
            rows={4}
            maxLength={MAX_FIELD_LENGTH_5000}
            onChange={(event) => {
              setPaymentComment(event.target.value);
            }}
          />
        </div>
      )}
    </Modal>
  );
};
